// Wayne AI Platform — onboarding Zustand slice (D-rebuild v3 / Stream C).
//
// UI-only state for the rebuilt onboarding flow. Server state belongs in
// TanStack Query; the `Me` payload is read from the `['me']` query and never
// duplicated here.
//
// State machine (post-D-rebuild):
//
//   dormant ─Begin discovery─► igniting ─1.2s─► live
//                                                 │
//                                                 │ all 14 connectors succeed
//                                                 ▼
//                                            completing
//                                                 │
//                                                 │ ~1.7s collapse anim
//                                                 ▼
//                                             welcomed
//
// `welcomed` ends with two CTAs: "Take the tour →" navigates home and seeds
// `sessionStorage['wayne.tour.start'] = 'home'`; "Skip to home" just navigates
// home. The Esc key short-circuits live → welcomed at any time.
//
// Storage key: `wayne.onboarding.v2`. We never persist row state — the
// connector list is cheap to re-derive and persisting in-flight timestamps
// from another tab confuses the visual budget.
//
// Connector state mirrors the OpenAPI `DiscoveryStatus` schema:
//   pending | running | succeeded | failed | skipped
//
// ADR-002: no Bindle-pick step. Memory and the user-graph are keyed on
// `employeeId`, not Bindle.

import { create } from 'zustand';

export const ONBOARDING_STAGES = [
  'dormant',
  'igniting',
  'live',
  'completing',
  'welcomed',
] as const;
export type OnboardingStage = (typeof ONBOARDING_STAGES)[number];

/** Mirrors the `state` enum on a single `DiscoveryStatus.connectors[i]`. */
export type ConnectorState =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped';

/** Stable connector id list from the OpenAPI spec (`DiscoveryStatus`). */
export const CONNECTOR_IDS = [
  'phonetool',
  'ldap',
  'teams',
  'bindles',
  'packages',
  'pipelines',
  'isengard',
  'taskei',
  'cti',
  'oncall',
  'slack',
  'sage',
  'quip',
  'kingpin',
] as const;
export type ConnectorId = (typeof CONNECTOR_IDS)[number];

export interface ConnectorRow {
  readonly id: ConnectorId;
  readonly label: string;
  state: ConnectorState;
  /** ms epoch the row entered `running`. Used for visual timing only. */
  startedAt?: number;
}

export interface OnboardingState {
  stage: OnboardingStage;
  connectors: Record<ConnectorId, ConnectorRow>;

  // -- mutators ------------------------------------------------------------
  setStage: (next: OnboardingStage) => void;
  setConnector: (id: ConnectorId, patch: Partial<ConnectorRow>) => void;
  setConnectors: (rows: Record<ConnectorId, ConnectorRow>) => void;
  reset: () => void;
}

const STORAGE_KEY = 'wayne.onboarding.v2';

interface PersistedShape {
  stage: OnboardingStage;
}

/** Display labels for each connector. Mirrors mockup language. */
export const CONNECTOR_LABELS: Readonly<Record<ConnectorId, string>> = {
  phonetool: 'Phonetool',
  ldap: 'LDAP',
  teams: 'Teams',
  bindles: 'Bindles',
  packages: 'Packages',
  pipelines: 'Pipelines',
  isengard: 'Isengard',
  taskei: 'Taskei',
  cti: 'CTI',
  oncall: 'Oncall',
  slack: 'Slack',
  sage: 'SAGE',
  quip: 'Quip',
  kingpin: 'Kingpin',
};

function makeInitialConnectors(): Record<ConnectorId, ConnectorRow> {
  const out = {} as Record<ConnectorId, ConnectorRow>;
  for (const id of CONNECTOR_IDS) {
    out[id] = { id, label: CONNECTOR_LABELS[id], state: 'pending' };
  }
  return out;
}

function readPersisted(): Partial<PersistedShape> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const obj = parsed as Record<string, unknown>;
    const out: Partial<PersistedShape> = {};
    if (
      typeof obj['stage'] === 'string' &&
      (ONBOARDING_STAGES as readonly string[]).includes(obj['stage'])
    ) {
      out.stage = obj['stage'] as OnboardingStage;
    }
    return out;
  } catch {
    return {};
  }
}

function writePersisted(state: PersistedShape): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage can throw under strict CSP / private mode; degrade silently.
  }
}

const initial = (): Pick<OnboardingState, 'stage' | 'connectors'> => {
  const persisted = readPersisted();
  return {
    stage: persisted.stage ?? 'dormant',
    connectors: makeInitialConnectors(),
  };
};

export const useOnboardingStore = create<OnboardingState>((set) => ({
  ...initial(),

  setStage: (next) => {
    set({ stage: next });
    writePersisted({ stage: next });
  },

  setConnector: (id, patch) => {
    set((state) => {
      const prev = state.connectors[id];
      if (!prev) return state;
      return {
        connectors: {
          ...state.connectors,
          [id]: { ...prev, ...patch },
        },
      };
    });
  },

  setConnectors: (rows) => {
    set({ connectors: rows });
  },

  reset: () => {
    set({
      stage: 'dormant',
      connectors: makeInitialConnectors(),
    });
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* see writePersisted */
      }
    }
  },
}));

/** Test-only helper. Resets both the store and the persisted shape. */
export function __resetOnboardingStoreForTests(): void {
  useOnboardingStore.getState().reset();
}
